import { useMemo } from "react";
import ReactECharts from "echarts-for-react";

import { Tooltip } from "@/components/Tooltip";
import type { WidgetComponentProps } from "../types";
import { useInsightsQuery } from "../useInsightsQuery";

export function SkillRadarWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useInsightsQuery();

  const option = useMemo(() => {
    const dims = data?.skill_profile.dimensions ?? [];
    return {
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(10, 14, 20, 0.96)",
        borderColor: "#2A3444",
        textStyle: { color: "#E8ECF1", fontSize: 12 },
      },
      radar: {
        indicator: dims.map((d) => ({ name: d.label, max: 1 })),
        shape: "polygon",
        splitNumber: 4,
        axisName: {
          color: "#A0AAB8",
          fontFamily: "Rajdhani, sans-serif",
          fontSize: 11,
          fontWeight: 600,
        },
        splitLine: { lineStyle: { color: "rgba(42, 52, 68, 0.6)" } },
        splitArea: {
          areaStyle: {
            color: ["rgba(255, 107, 26, 0.02)", "rgba(255, 107, 26, 0.04)"],
          },
        },
        axisLine: { lineStyle: { color: "rgba(42, 52, 68, 0.8)" } },
      },
      series: [
        {
          type: "radar",
          data: [
            {
              value: dims.map((d) => d.value),
              name: "You",
              areaStyle: { color: "rgba(255, 107, 26, 0.25)" },
              lineStyle: { color: "#FF6B1A", width: 2 },
              itemStyle: { color: "#FF6B1A" },
            },
          ],
          emphasis: { lineStyle: { width: 3 } },
        },
      ],
    };
  }, [data]);

  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const dims = data?.skill_profile.dimensions ?? [];
  if (!dims.length) return <p className="widget-empty">Not enough flights.</p>;

  return (
    <div className={`skill-radar skill-radar--${sizeVariant}`}>
      <div className="skill-radar__head">
        <div>
          <p className="eyebrow">
            Skill profile
            <Tooltip
              content={
                <>
                  <strong>Your pilot radar</strong>
                  <span>
                    Five dimensions normalized 0–1 from your last {data?.skill_profile.flights_sampled} flights.
                    Higher is better for each axis.
                  </span>
                </>
              }
            >
              <span className="info-hint" aria-label="About radar">i</span>
            </Tooltip>
          </p>
          <h3 className="skill-radar__title">Pilot capability</h3>
        </div>
        <span className="skill-radar__sample-note">
          {data?.skill_profile.flights_sampled} flights sampled
        </span>
      </div>
      <div className="skill-radar__chart">
        <ReactECharts
          option={option}
          notMerge
          style={{ height: "100%", width: "100%" }}
        />
      </div>
      {sizeVariant === "l" && (
        <ul className="skill-radar__legend">
          {dims.map((d) => (
            <li key={d.key}>
              <span className="skill-radar__legend-lbl">{d.label}</span>
              <span className="skill-radar__legend-val">
                {(d.value * 100).toFixed(0)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
